<script setup lang="ts">
import { useNotificationsStore } from '@/stores/notifications';

const notifications = useNotificationsStore();

function iconFor(level: string): string {
  switch (level) {
    case 'success': return '✓';
    case 'warning': return '⚠';
    case 'error':   return '✕';
    default:        return 'ℹ';
  }
}
</script>

<template>
  <Teleport to="body">
    <div class="toast-stack" aria-live="polite">
      <TransitionGroup name="toast">
        <div
          v-for="n in notifications.items"
          :key="n.id"
          :class="['toast', n.level]"
          role="alert"
        >
          <span class="icon">{{ iconFor(n.level) }}</span>
          <div class="content">
            <strong class="title">{{ n.title }}</strong>
            <p v-if="n.message" class="message">{{ n.message }}</p>
          </div>
          <button class="close" @click="notifications.dismiss(n.id)" aria-label="Schließen">×</button>
        </div>
      </TransitionGroup>
    </div>
  </Teleport>
</template>

<style scoped>
.toast-stack {
  position: fixed;
  top: 1rem;
  right: 1rem;
  z-index: 10000;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-width: 360px;
  pointer-events: none;
}
.toast {
  pointer-events: auto;
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  background: white;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12), 0 2px 6px rgba(0, 0, 0, 0.08);
  border-left: 4px solid;
  font-size: 0.9rem;
}
.toast.info    { border-left-color: #2563eb; }
.toast.success { border-left-color: #10b981; }
.toast.warning { border-left-color: #f59e0b; }
.toast.error   { border-left-color: #dc2626; }

.icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem; height: 1.5rem;
  border-radius: 50%;
  font-weight: 700;
  flex-shrink: 0;
}
.toast.info    .icon { background: #dbeafe; color: #1e40af; }
.toast.success .icon { background: #dcfce7; color: #166534; }
.toast.warning .icon { background: #fef3c7; color: #92400e; }
.toast.error   .icon { background: #fee2e2; color: #991b1b; }

.content { flex: 1; min-width: 0; }
.title { display: block; }
.message {
  margin: 0.2rem 0 0;
  color: #6b7280;
  font-size: 0.85rem;
  line-height: 1.4;
  word-break: break-word;
}
.close {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1.3rem;
  line-height: 1;
  color: #9ca3af;
  padding: 0 0.25rem;
}
.close:hover { color: #374151; }

.toast-enter-active, .toast-leave-active {
  transition: all 0.25s ease;
}
.toast-enter-from {
  opacity: 0;
  transform: translateX(20px);
}
.toast-leave-to {
  opacity: 0;
  transform: translateX(20px);
}
</style>
